/**
 * Per-location weekly operating-hours grid (DNKADM-0051 U2) — the editable 7-row
 * multi-shift schedule for `GET/PUT /v2/admin/locations/{id}/hours`, seeded from the
 * loaded view and saved through `putOperatingHours`.
 *
 * Seven rows in `dayOfWeek` order **0=Sunday … 6=Saturday** (the §5.6 staff/resource
 * convention this shares): each row an "Open" toggle and, when open, 1..n shift rows
 * — each a native `<TextField type="time">` start + end (HH:mm, location-local) with
 * add/remove. A single **"Save hours"** action `PUT`s all seven days at once.
 *
 * Client validation mirrors the server (`validateHours.ts`): an open day needs ≥1
 * shift, each shift `start < end` (overnight disallowed), no overlap/duplicate. A
 * failing day shows its inline error and BLOCKS Save — no PUT reaches the wire while
 * invalid. On a successful save the parent resource is `refetch`ed and a success
 * toast is raised; a validation `400` renders INLINE, anything else raises a
 * non-blocking error toast (the shared `classifyFormError`).
 *
 * Native MUI time inputs only — NO `@mui/x-date-pickers` (not in the fleet baseline).
 *
 * **Reusable outside the location context (DNKADM-0315):** a caller may pass `onSave`
 * (the whole-week persist for a different endpoint — the per-plan access-hours cap)
 * instead of `locationId`, plus optional `saveLabel` / `savedMessage` copy and an
 * `extraActions` slot rendered beside Save. The rows, validation, copy-day affordance
 * and inline/toast error split are identical in every host — one grid, no fork.
 *
 * **Embedded in a drawer (DNKADM-0495).** With `embedded`, the grid renders no Save of its
 * own: the location page's Open hours drawer owns the one Save and drives it through
 * `handleRef`. Same validation, same PUT body; a failure of either kind shows inline (the
 * drawer stays open on it) and the drawer raises the success toast.
 */
import { useImperativeHandle, useState, type ReactNode, type Ref } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconAction } from '@/components/IconAction';
import { Icon, resolveTokens } from '@/theme';
import type { HoursShift, WeeklyHoursDay } from '@/api/operatingHours';
import { putOperatingHours } from '@/api/operatingHours';
import { copyDay } from '@/utils/scheduleCopy';
import { CopyDayButton } from '@/components/CopyDayButton';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';
import { validateWeeklyHours, type DayErrors } from './validateHours';

/** Sun–Sat day labels indexed by `dayOfWeek` (0=Sunday … 6=Saturday). */
const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** A blank shift for a freshly-opened day / an added shift row. */
function blankShift(): HoursShift {
  return { startTime: '', endTime: '' };
}

/**
 * Where the whole-week Save goes: the location hours endpoint (`locationId`) — the
 * original host — or a caller-supplied persist (`onSave`) for another weekly-hours
 * surface sharing the same day/shift shape. Exactly one of the two.
 */
type SaveTarget =
  | {
      /** The selected owned location's id, forwarded on PUT. */
      locationId: string;
      onSave?: undefined;
    }
  | {
      locationId?: undefined;
      /** Persist all seven days; throw an `ApiError` on failure (classified like the location save). */
      onSave: (days: WeeklyHoursDay[]) => Promise<void>;
    };

/** The drawer's handle on an embedded grid (DNKADM-0495). */
export interface OperatingHoursGridHandle {
  /** Validate and PUT the week. `true` = saved; `false` = not saved (the grid says why). */
  save: () => Promise<boolean>;
}

type OperatingHoursGridProps = SaveTarget & {
  /** The loaded weekly schedule (always seven days in `dayOfWeek` 0→6 order). */
  days: WeeklyHoursDay[];
  /** Re-fetch after a successful save (refetch-after-write). */
  refetch: () => void;
  /** Raise a non-blocking toast (success / upstream-error). */
  showToast: ShowToast;
  /** Save button label (default "Save hours"). */
  saveLabel?: string;
  /** Success toast after a save (default "Hours saved"). */
  savedMessage?: string;
  /** Extra action(s) rendered beside Save (e.g. a destructive "Remove configuration"). */
  extraActions?: ReactNode;
  /** Render without a Save button; the owning drawer saves through `handleRef` (DNKADM-0495). */
  embedded?: boolean;
  /** The drawer's handle (embedded mode). */
  handleRef?: Ref<OperatingHoursGridHandle>;
};

export function OperatingHoursGrid(props: OperatingHoursGridProps) {
  const {
    days: initialDays,
    refetch,
    showToast,
    saveLabel = 'Save hours',
    savedMessage = 'Hours saved',
    extraActions,
    embedded = false,
    handleRef,
  } = props;
  // Edit the seven rows locally; the parent remounts this on each (re)load / location
  // change so the rows reseed from the freshly loaded view.
  const [days, setDays] = useState<WeeklyHoursDay[]>(initialDays);
  const [dayErrors, setDayErrors] = useState<DayErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function updateDay(dayOfWeek: number, patch: Partial<WeeklyHoursDay>) {
    setDays((prev) => prev.map((d) => (d.dayOfWeek === dayOfWeek ? { ...d, ...patch } : d)));
  }

  function handleToggleAvailable(dayOfWeek: number, available: boolean) {
    // Opening a day seeds one blank shift to fill in; closing clears its shifts.
    updateDay(
      dayOfWeek,
      available
        ? { isAvailable: true, shifts: [blankShift()] }
        : { isAvailable: false, shifts: [] },
    );
  }

  function updateShift(dayOfWeek: number, index: number, patch: Partial<HoursShift>) {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? { ...d, shifts: d.shifts.map((s, i) => (i === index ? { ...s, ...patch } : s)) }
          : d,
      ),
    );
  }

  function addShift(dayOfWeek: number) {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek ? { ...d, shifts: [...d.shifts, blankShift()] } : d,
      ),
    );
  }

  function removeShift(dayOfWeek: number, index: number) {
    setDays((prev) =>
      prev.map((d) =>
        d.dayOfWeek === dayOfWeek
          ? { ...d, shifts: d.shifts.filter((_s, i) => i !== index) }
          : d,
      ),
    );
  }

  // Copy one day's open state + (cloned) shifts into the chosen target days,
  // staging the change for the existing whole-week Save (no extra request).
  function copyDayTo(sourceDow: number, targetDows: number[]) {
    setDays((prev) => copyDay(prev, sourceDow, targetDows));
  }

  async function handleSave(): Promise<boolean> {
    setServerError(null);
    const result = validateWeeklyHours(days);
    if (!result.ok) {
      setDayErrors(result.errors);
      return false;
    }
    setDayErrors({});
    setSaving(true);
    try {
      // The save target is a discriminated union — narrow on the prop, not a destructure.
      if (props.onSave !== undefined) await props.onSave(days);
      else await putOperatingHours(days, props.locationId);
      // Refetch-after-write: the grid reseeds from the freshly saved view.
      refetch();
      if (!embedded) showToast(savedMessage, 'success');
      return true;
    } catch (err) {
      const view = classifyFormError(err, 'Those hours aren’t valid — check the highlighted days.', {
        toastFallback: 'The hours couldn’t be saved. Please try again.',
        conflictInline: false,
      });
      // Embedded: the drawer stays open on the failure, so it is said right here.
      if (view.kind === 'inline' || embedded) setServerError(view.message);
      else showToast(view.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }

  useImperativeHandle(handleRef, () => ({ save: handleSave }));

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Paper elevation={1} sx={{ px: 2.75, py: 0.75 }}>
        {days.map((day, i) => (
          <DayRow
            key={day.dayOfWeek}
            label={DAY_LABELS[day.dayOfWeek] ?? `Day ${day.dayOfWeek}`}
            dayLabels={DAY_LABELS}
            day={day}
            error={dayErrors[day.dayOfWeek]}
            disabled={saving}
            last={i === days.length - 1}
            onToggleAvailable={(available) => handleToggleAvailable(day.dayOfWeek, available)}
            onShiftChange={(index, patch) => updateShift(day.dayOfWeek, index, patch)}
            onAddShift={() => addShift(day.dayOfWeek)}
            onRemoveShift={(index) => removeShift(day.dayOfWeek, index)}
            onCopyDay={(targetDows) => copyDayTo(day.dayOfWeek, targetDows)}
          />
        ))}
      </Paper>
      {serverError && (
        <Box role="alert" sx={{ mt: 1.5, fontSize: 13, color: 'error.main', fontWeight: 500 }}>
          {serverError}
        </Box>
      )}
      {!embedded && (
        <Stack
          direction="row"
          sx={{ justifyContent: 'flex-end', alignItems: 'center', gap: 1.5, mt: 1.75 }}
        >
          {extraActions}
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : saveLabel}
          </Button>
        </Stack>
      )}
    </Box>
  );
}

/** One day's row: the day label + Open toggle, and (when open) its shift rows. */
function DayRow({
  label,
  dayLabels,
  day,
  error,
  disabled,
  last,
  onToggleAvailable,
  onShiftChange,
  onAddShift,
  onRemoveShift,
  onCopyDay,
}: {
  label: string;
  dayLabels: string[];
  day: WeeklyHoursDay;
  error: string | undefined;
  disabled: boolean;
  last: boolean;
  onToggleAvailable: (available: boolean) => void;
  onShiftChange: (index: number, patch: Partial<HoursShift>) => void;
  onAddShift: () => void;
  onRemoveShift: (index: number) => void;
  onCopyDay: (targetDows: number[]) => void;
}) {
  const t = resolveTokens(useTheme());
  return (
    <Box
      sx={{
        py: 2,
        borderBottom: last ? 'none' : '1px solid',
        borderColor: t.lineFaint,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 500, minWidth: 96, pt: 1 }}>
          {label}
        </Typography>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={day.isAvailable}
                onChange={(e) => onToggleAvailable(e.target.checked)}
                disabled={disabled}
                slotProps={{ input: { 'aria-label': `${label} open` } }}
              />
            }
            label={day.isAvailable ? 'Open' : 'Closed'}
            sx={{ mb: day.isAvailable ? 1 : 0 }}
          />
          {day.isAvailable &&
            day.shifts.map((shift, index) => (
              <Stack
                // Shift rows are positional; index keys are correct (add/remove rebuilds the list).
                key={index}
                direction="row"
                sx={{ alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}
              >
                <TextField
                  type="time"
                  label="Start"
                  size="small"
                  value={shift.startTime}
                  onChange={(e) => onShiftChange(index, { startTime: e.target.value })}
                  disabled={disabled}
                  error={Boolean(error)}
                  sx={{ width: 140 }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      'aria-label': `${label} shift ${index + 1} start time (HH:mm, local)`,
                    },
                  }}
                />
                <TextField
                  type="time"
                  label="End"
                  size="small"
                  value={shift.endTime}
                  onChange={(e) => onShiftChange(index, { endTime: e.target.value })}
                  disabled={disabled}
                  error={Boolean(error)}
                  sx={{ width: 140 }}
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      'aria-label': `${label} shift ${index + 1} end time (HH:mm, local)`,
                    },
                  }}
                />
                <IconAction
                  size="small"
                  label={`Remove ${label} shift ${index + 1}`}
                  onClick={() => onRemoveShift(index)}
                  disabled={disabled || day.shifts.length === 1}
                >
                  <Icon name="close" sx={{ fontSize: 18 }} />
                </IconAction>
              </Stack>
            ))}
          {day.isAvailable && (
            <Button
              size="small"
              startIcon={<Icon name="add" sx={{ fontSize: 18 }} />}
              onClick={onAddShift}
              disabled={disabled}
              sx={{ textTransform: 'none' }}
            >
              Add shift
            </Button>
          )}
        </Box>
        <Box sx={{ pt: 0.5 }}>
          <CopyDayButton
            sourceDow={day.dayOfWeek}
            dayLabels={dayLabels}
            disabled={disabled}
            onApply={onCopyDay}
          />
        </Box>
      </Stack>
      {error && (
        <Typography role="alert" sx={{ mt: 0.75, fontSize: 12, color: 'error.main' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
